/* eslint-disable @typescript-eslint/no-non-null-assertion */

'use client'

import type { SerializedEditorState } from '@payloadcms/richtext-lexical/lexical'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AlertCircle, Flag, Settings2 } from 'lucide-react'

import type { RenderLink } from '@ui/web/utils'
import { Button } from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import cn from '@config/tailwind/cn'

import type { ScoreboardType } from './index'
import { IndexNav } from '../../../components/index-nav'
import { LESSON_TYPE_INFO } from '../../../components/lesson-overview-button'
import { TeacherGuide } from '../../../components/teacher-guide'
import { useSharedTRPC } from '../../../context'
import { EditCourseDialog } from '../../../lib/edit-course/edit-course-dialog'

type HeaderProps = ScoreboardType & {
  onLessonChange: (lessonId: string) => void
  onLessonTypeChange?: (type: string | null) => void
  onLessonIndexChange?: (index: number) => void
  initialLessonIndex?: number
  renderLessonLink?: RenderLink
  className?: string
}

const LESSON_TYPE_KEY = {
  DISCOVERY: 'discovery',
  IN_CLASS: 'in-class',
  READ: 'read',
  QUIZ: 'quiz',
} as const

export function Header({
  onLessonChange,
  onLessonTypeChange,
  onLessonIndexChange,
  renderLessonLink,
  initialLessonIndex,
  className,
  ...p
}: HeaderProps) {
  const trpc = useSharedTRPC()

  const [currentLessonId, setCurrentLessonId] = useState<string | null>(null)
  const [editOpen, setEditOpen] = useState(false)
  const [guideOpen, setGuideOpen] = useState(false)

  const initialIndexRef = useRef(initialLessonIndex ?? 1)

  const { data, isPending, isError, refetch } = useQuery({
    ...trpc.lesson.getLessonModuleDetails.queryOptions({
      challengeId: p.type === 'challenge' ? p.challengeId : undefined,
      courseId: p.type === 'course' ? p.courseId : undefined,
    }),
    staleTime: Infinity,
  })

  const whichLesson = useMemo(() => {
    if (!data) return null

    const currentIndex = data.lessons.findIndex((lesson) => lesson.lesson.id === currentLessonId)
    if (currentIndex === -1) return null

    return { ...data.lessons[currentIndex]!, lessonIndex: currentIndex + 1 }
  }, [currentLessonId, data])

  const handlePreviousLesson = useCallback(() => {
    if (!data) return
    const currentIndex = data.lessons.findIndex((lesson) => lesson.lesson.id === currentLessonId)
    if (currentIndex > 0) {
      const newIdx = currentIndex - 1
      setCurrentLessonId(data.lessons[newIdx]!.lesson.id)
      onLessonChange(data.lessons[newIdx]!.lesson.id)
      onLessonTypeChange?.(data.lessons[newIdx]!.lesson.type)
      onLessonIndexChange?.(newIdx + 1)
    }
  }, [currentLessonId, data, onLessonChange, onLessonTypeChange, onLessonIndexChange])

  const handleNextLesson = useCallback(() => {
    if (!data) return
    const currentIndex = data.lessons.findIndex((lesson) => lesson.lesson.id === currentLessonId)
    if (currentIndex < data.lessons.length - 1) {
      const newIdx = currentIndex + 1
      setCurrentLessonId(data.lessons[newIdx]!.lesson.id)
      onLessonChange(data.lessons[newIdx]!.lesson.id)
      onLessonTypeChange?.(data.lessons[newIdx]!.lesson.type)
      onLessonIndexChange?.(newIdx + 1)
    }
  }, [currentLessonId, data, onLessonChange, onLessonTypeChange, onLessonIndexChange])

  const handleFirstLesson = useCallback(() => {
    if (!data || data.lessons.length === 0) return
    const first = data.lessons[0]!
    setCurrentLessonId(first.lesson.id)
    onLessonChange(first.lesson.id)
    onLessonTypeChange?.(first.lesson.type)
    onLessonIndexChange?.(1)
  }, [data, onLessonChange, onLessonTypeChange, onLessonIndexChange])

  const handleLastLesson = useCallback(() => {
    if (!data || data.lessons.length === 0) return
    const last = data.lessons[data.lessons.length - 1]!
    setCurrentLessonId(last.lesson.id)
    onLessonChange(last.lesson.id)
    onLessonTypeChange?.(last.lesson.type)
    onLessonIndexChange?.(data.lessons.length)
  }, [data, onLessonChange, onLessonTypeChange, onLessonIndexChange])

  const handleLessonIndexChange = useCallback(
    (index: number) => {
      if (!data || data.lessons.length === 0) return
      const lesson = data.lessons[index - 1]!
      setCurrentLessonId(lesson.lesson.id)
      onLessonChange(lesson.lesson.id)
      onLessonTypeChange?.(lesson.lesson.type)
      onLessonIndexChange?.(index)
    },
    [data, onLessonChange, onLessonTypeChange, onLessonIndexChange],
  )

  // Set initial lesson when data loads
  useEffect(() => {
    if (data && data.lessons.length > 0 && !currentLessonId) {
      const targetIdx = Math.min(Math.max(initialIndexRef.current - 1, 0), data.lessons.length - 1)
      const lesson = data.lessons[targetIdx]!
      setCurrentLessonId(lesson.lesson.id)
      onLessonChange(lesson.lesson.id)
      onLessonTypeChange?.(lesson.lesson.type)
    }
  }, [data, currentLessonId, onLessonChange, onLessonTypeChange])

  if (isError) {
    return (
      <section className={cn('srd:w-full srd:flex srd:flex-col srd:gap-y-4 srd:px-4', className)}>
        <div className='srd:flex srd:items-center srd:justify-between'>
          <p className={cn(typographyVariants({ variant: 'title-md' }))}>Penilaian</p>
        </div>
        <section className='srd:p-4 srd:border srd:border-error srd:rounded-md srd:bg-error/20 srd:w-full srd:flex srd:flex-col srd:gap-3'>
          <div className='srd:flex srd:items-center srd:gap-2 srd:text-neutral-white'>
            <AlertCircle className='srd:size-5 srd:shrink-0' />
            <p className={typographyVariants({ variant: 'body-sm' })}>Gagal memuat detail modul. Silakan coba lagi.</p>
          </div>
          <Button variant='secondary' size='sm' onClick={() => void refetch()} className='srd:w-fit'>
            Coba lagi
          </Button>
        </section>
      </section>
    )
  }

  if (isPending || !whichLesson) {
    return (
      <section className={cn('srd:w-full srd:flex srd:flex-col srd:gap-y-4 srd:px-4', className)}>
        <section className='srd:flex srd:items-center srd:justify-between srd:w-full'>
          <div className='srd:h-4 srd:w-24 srd:rounded srd:bg-neutral-white/20 srd:animate-pulse' />
          <div className='srd:h-8 srd:w-36 srd:rounded-lg srd:bg-neutral-white/20 srd:animate-pulse' />
        </section>
        <section className='srd:flex srd:flex-col srd:gap-y-2'>
          <div className='srd:h-3 srd:w-48 srd:rounded srd:bg-neutral-white/20 srd:animate-pulse' />
          <div className='srd:h-6 srd:w-64 srd:rounded srd:bg-neutral-white/20 srd:animate-pulse' />
          <div className='srd:h-3 srd:w-32 srd:rounded srd:bg-neutral-white/20 srd:animate-pulse' />
        </section>
        <section className='srd:flex srd:items-center srd:gap-x-1'>
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={`skeleton-nav-${i}`}
              className='srd:w-10 srd:h-10 srd:rounded-full srd:bg-neutral-white/20 srd:animate-pulse'
            />
          ))}
        </section>
      </section>
    )
  }

  return (
    <section className={cn('srd:w-full srd:flex srd:flex-col srd:gap-y-4 srd:px-4', className)}>
      <div className='srd:flex srd:flex-col srd:gap-y-1'>
        {/* Row 1: page label + settings button */}
        <div className='srd:flex srd:items-center srd:justify-between srd:gap-x-4 srd:gap-y-1 srd:flex-wrap'>
          <p className={cn(typographyVariants({ variant: 'title-md' }))}>Penilaian</p>

          {p.type === 'course' && (
            <Button variant='outline' className='srd:text-neutral-white' onClick={() => setEditOpen(true)}>
              <Settings2 className='srd:size-5 srd:mr-2' />
              Pengaturan tugas
            </Button>
          )}
        </div>

        {/* Row 2: title block */}
        <p className={cn(typographyVariants({ variant: 'title-sm' }), 'srd:truncate')}>{data.title}</p>
        <h1 className={cn(typographyVariants({ variant: 'headline-sm' }), 'srd:line-clamp-2')}>
          {whichLesson.lesson.title}
        </h1>
        <p className={cn(typographyVariants({ variant: 'body-lg' }), 'srd:text-grey-100')}>
          Aktivitas {whichLesson.order + 1}: {LESSON_TYPE_INFO[LESSON_TYPE_KEY[whichLesson.lesson.type]].label}
        </p>
      </div>

      {/* Row 3: lesson navigation */}
      <div className='srd:flex srd:items-center srd:gap-2 srd:flex-wrap'>
        <Button
          variant='ghost'
          aria-label='Panduan guru'
          className='srd:w-14 srd:h-12 srd:p-0 srd:text-neutral-white srd:hover:bg-navy-80 srd:rounded-full srd:bg-navy-100'
          onClick={() => setGuideOpen(true)}
        >
          <Flag className='srd:size-5' />
        </Button>

        <IndexNav
          current={whichLesson.lessonIndex}
          total={data.lessons.length}
          onFirst={handleFirstLesson}
          onPrevious={handlePreviousLesson}
          onNext={handleNextLesson}
          onLast={handleLastLesson}
          onChange={handleLessonIndexChange}
        />
      </div>

      {/* EditCourseDialog — only rendered for course type */}
      {p.type === 'course' && data.type === 'course' && (
        <EditCourseDialog
          type={p.from === 'school' ? 'school' : 'web'}
          open={editOpen}
          onOpenChange={setEditOpen}
          courseId={p.courseId}
          initialTitle={data.title}
          initialStartDate={data.startDate}
          initialEndDate={data.endDate}
          initialCreatedAt={data.createdAt}
          initialParticipantIds={data.learnEnrollments.map((e) => e.childId)}
          onSuccess={() => void refetch()}
        />
      )}

      <TeacherGuide
        open={guideOpen}
        onOpenChange={setGuideOpen}
        title={whichLesson.lesson.title}
        lessonType={LESSON_TYPE_KEY[whichLesson.lesson.type]}
        teacherGuide={whichLesson.lesson.guide as SerializedEditorState | null}
      >
        {renderLessonLink && (
          <Button variant='outline' asChild onClick={() => setGuideOpen(false)}>
            {renderLessonLink({
              meta: {
                lessonId: whichLesson.lesson.id,
                lessonIndex: String(whichLesson.lessonIndex),
                ...(p.type === 'course' ? { courseId: p.courseId } : {}),
                ...(p.type === 'challenge' ? { challengeId: p.challengeId } : {}),
              },
              children: 'Pratinjau tampilan murid',
            })}
          </Button>
        )}
      </TeacherGuide>
    </section>
  )
}
